import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { billingApi, billingQueryKeys } from "../../../shared/api/billing";
import { apiError } from "../../../shared/api/client";
import { formatVndWithUnit, paymentMethodLabel } from "../../../shared/format";
import type {
  InvoiceDetail,
  PaymentMethod,
} from "../../../shared/types/billing";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { TransferQr } from "./TransferQr";
import { randomUuid } from "../../../shared/randomUuid";

interface Props {
  invoice: InvoiceDetail;
  onClose: () => void;
}

const METHODS: PaymentMethod[] = ["CASH", "CARD", "BANK_TRANSFER", "VNPAY", "MOMO"];

export function RecordPaymentModal({ invoice, onClose }: Props) {
  const qc = useQueryClient();
  const [method, setMethod] = useState<PaymentMethod>("CASH");
  const [amount, setAmount] = useState<number>(invoice.balanceDue);
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  // MỘT khoá cho cả lần mở hộp thoại này, không phải mỗi lần bấm một khoá mới. Khoá mới mỗi lần
  // bấm chỉ chặn được mạng gửi lại; nó KHÔNG chặn được người bấm hai lần, vì hai cú bấm là hai
  // yêu cầu khác nhau với hai khoá khác nhau — và tiền vào hoá đơn hai lần.
  const [idempotencyKey] = useState(() => randomUuid());

  const mut = useMutation({
    mutationFn: () =>
      billingApi.recordPayment({
        invoiceId: invoice.id,
        method,
        amount,
        reference: method === "CASH" ? null : reference || null,
        note: note || null,
      },
      idempotencyKey),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: billingQueryKeys.invoiceDetail(invoice.id) });
      qc.invalidateQueries({ queryKey: ["billing", "invoices"] });
      qc.invalidateQueries({ queryKey: ["billing", "payments"] });
      onClose();
    },
    onError: (e) => setError(apiError(e).detail),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (amount <= 0) {
      setError("Số tiền phải lớn hơn 0");
      return;
    }
    if (amount > invoice.balanceDue) {
      setError(`Số tiền vượt quá còn lại (${formatVndWithUnit(invoice.balanceDue)})`);
      return;
    }
    mut.mutate();
  }

  return (
    <Dialog open onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent aria-label="Ghi nhận thanh toán">
        <DialogHeader>
          <DialogTitle>Ghi nhận thanh toán</DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-4 rounded-md border bg-muted/40 px-4 py-3">
            <div className="space-y-0.5">
              <span className="block text-xs text-muted-foreground">Hoá đơn</span>
              <strong className="font-mono text-sm text-foreground">{invoice.code}</strong>
            </div>
            <div className="space-y-0.5 text-right">
              <span className="block text-xs text-muted-foreground">Còn phải thu</span>
              <strong className="text-sm text-amber-700">
                {formatVndWithUnit(invoice.balanceDue)}
              </strong>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Phương thức</Label>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {METHODS.map((m) => (
                <Button
                  key={m}
                  type="button"
                  variant={method === m ? "default" : "outline"}
                  size="sm"
                  onClick={() => setMethod(m)}
                >
                  {paymentMethodLabel[m]}
                </Button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="payment-amount">Số tiền (₫)</Label>
            <Input
              id="payment-amount"
              type="number"
              min={0}
              max={invoice.balanceDue}
              step={1000}
              value={amount}
              onChange={(e) => setAmount(Math.max(0, Number(e.target.value)))}
              autoFocus
              required
            />
            <div>
              <Button
                type="button"
                variant="link"
                size="sm"
                className="h-auto p-0"
                onClick={() => setAmount(invoice.balanceDue)}
              >
                Thu hết số còn lại
              </Button>
            </div>
          </div>

          {method === "BANK_TRANSFER" && (
            <div className="space-y-1.5">
              <Label>Mã QR cho bệnh nhân quét</Label>
              <TransferQr invoiceId={invoice.id} />
            </div>
          )}

          {method !== "CASH" && (
            <div className="space-y-1.5">
              <Label htmlFor="payment-reference">Số tham chiếu</Label>
              <Input
                id="payment-reference"
                type="text"
                value={reference}
                placeholder="Mã giao dịch / 4 số cuối thẻ / ref VNPAY…"
                onChange={(e) => setReference(e.target.value)}
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="payment-note">Ghi chú</Label>
            <Textarea
              id="payment-note"
              value={note}
              rows={2}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>

          {error && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Hủy
            </Button>
            <Button type="submit" disabled={mut.isPending}>
              {mut.isPending ? "Đang lưu…" : "Xác nhận thu"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
